import {
	useCallback,
	useMemo,
	useState
} from "react";

export interface BreadcrumbFolder {
	id: string | undefined;
	name: string;
}

export type FolderNavigationStack<TFolderModel extends BreadcrumbFolder> = {
	items: readonly TFolderModel[];
	reset(): void;
	set(items: TFolderModel[]): void;
	navigateForwardTo(item: TFolderModel): void;
	navigateBackTo(folderId: string | undefined): void;
};

export function useFolderNavigationStack<TFolderModel extends BreadcrumbFolder>()
	: FolderNavigationStack<TFolderModel> {

	const [items, setItems] = useState<readonly TFolderModel[]>([]);

	const reset = useCallback(
		(): void => {
			setItems([]);
		},
		[]
	);

	const navigateForwardTo = useCallback(
		(item: TFolderModel): void => {
			setItems(
				(current) => {
					return [...current, item];
				}
			);
		},
		[]
	);

	const navigateBackTo = useCallback(
		(folderId: string): void => {
			setItems(
				(current) => {
					const copy: TFolderModel[] = [...current];

					const index: number = copy.findIndex(x => x.id === folderId);

					if (index === -1) {
						throw new Error("Folder is not present on the stack");
					}

					const length: number = copy.length;
					if (index === length) {
						throw new Error("Can't navigate back to the current folder on the stack");
					}

					copy.splice(index + 1);

					return copy;
				}
			);
		},
		[]
	);

	return useMemo(
		(): FolderNavigationStack<TFolderModel> => {
			return {
				items,
				reset,
				set: setItems,
				navigateForwardTo,
				navigateBackTo
			};
		},
		[
			items,
			navigateBackTo,
			navigateForwardTo,
			reset
		]
	);
};
